import moment from "moment";
import {
  ComplianceStatusType,
  ResultStatusType,
  StatusType,
  ThemeMode,
  getStatusDefinition,
  getStatusDotColor,
  getStatusMainColor,
  getStatusStyle,
  compareCompliancePriority,
  compareResultPriority,
  getCompliancePriority,
  getResultPriority,
  renderStatusIcon,
  getStatusIconComponent,
} from "../constants/status";
import { WidgetTestResult } from "../models/machine-test-result.model";
import { TestRunStatus } from "../models/test-run.model";

export function resolveThemeMode(paletteType?: string): ThemeMode {
  return paletteType === "dark" ? "dark" : "light";
}

export function getStatusColor(status: StatusType, mode: ThemeMode): string {
  return getStatusMainColor(status, mode);
}

export function getStatusLabel(status: StatusType): string {
  return getStatusDefinition(status).label;
}

export function getStatusTooltip(status: StatusType): string | undefined {
  return getStatusDefinition(status).tooltip;
}

export function getStatusPriority(status: StatusType): number {
  if (status === "pass" || status === "fail" || status === "failed_resolved") {
    return getResultPriority(status);
  }
  return getCompliancePriority(status as ComplianceStatusType);
}

export { getStatusStyle, getStatusDotColor, getStatusIconComponent, renderStatusIcon };

export { getStatusIconColor } from "../constants/status/statusColors";

export function aggregateComplianceStatuses(statuses: ComplianceStatusType[]): ComplianceStatusType {
  if (!statuses.length) {
    return "complete";
  }
  return statuses.reduce((highest, current) =>
    compareCompliancePriority(current, highest) > 0 ? current : highest
  );
}

export function aggregateResultStatuses(statuses: ResultStatusType[]): ResultStatusType {
  if (!statuses.length) {
    return "pass";
  }
  return statuses.reduce((highest, current) =>
    compareResultPriority(current, highest) > 0 ? current : highest
  );
}

export interface DeviceTestComplianceInput {
  dueAt?: Date | string | null;
  due?: boolean;
  overdue?: boolean;
  partial?: boolean;
  daysSinceLastRun?: number | null;
  lastTestRun?: Date | string | null;
  frequency?: number;
  status?: string;
  /**
   * Exact-match "Next_Due_Date == Current Local Date" (Story #12572) - distinct from `due` above,
   * which keeps its original "due or overdue" meaning (used for the Due/Overdue filters).
   */
  dueToday?: boolean;
  /** Compliance status flags (Story #12572), computed server-side in the facility's local timezone. */
  missed?: boolean;
  incomplete?: boolean;
  pending?: boolean;
  skipped?: boolean;
}

/**
 * QA Details status input (Story #12572).
 * Uses the due/missed/incomplete/pending/skipped flags from getQATests (existing API fields,
 * computed server-side against each facility's local calendar day).
 */
export interface QADetailsStatusInput extends DeviceTestComplianceInput {}

/** Checked in this order - the highest-priority true flag wins (see COMPLIANCE_STATUS_PRIORITY). */
const QA_COMPLIANCE_FLAG_KEYS: ComplianceStatusType[] = ["missed", "incomplete", "pending", "skipped"];

/**
 * QA Details status column (Story #12572).
 *
 * Backend computes each condition independently (missed/incomplete/pending/skipped, plus due) in
 * the facility's local timezone; this picks the highest-priority one that applies -
 * Missed > Incomplete > Pending > Skipped > Due > Not Due (see COMPLIANCE_STATUS_PRIORITY).
 *
 * `dueToday` is an exact match only (Next_Due_Date == Current Local Date) - a test that's overdue
 * by several days falls through to Missed/Incomplete above instead, and everything else (due date
 * still ahead, with none of the other flags active) is Not Due. Note this is `dueToday`, not the
 * plain `due` flag - `due` keeps its original "due or overdue" meaning for the Due/Overdue filters.
 */
export function getQADetailsStatus(test: QADetailsStatusInput): StatusType {
  const activeStatuses = QA_COMPLIANCE_FLAG_KEYS.filter((key) => !!(test as Record<ComplianceStatusType, boolean | undefined>)[key]);

  if (activeStatuses.length) {
    return aggregateComplianceStatuses(activeStatuses);
  }

  return test.dueToday ? "due" : "not_due";
}

export function getQADetailsStatusLabel(test: QADetailsStatusInput): string {
  const readyStatus = (test.status ?? "ready").toLowerCase();

  // Non-ready setup states keep their raw API status text (e.g. "Procedure is not set")
  if (readyStatus !== "ready") {
    return test.status || getStatusLabel(getQADetailsStatus(test));
  }

  return getStatusLabel(getQADetailsStatus(test));
}

export function getQADetailsStatusTooltip(test: QADetailsStatusInput): string | undefined {
  const status = getQADetailsStatus(test);
  const dueAt = test.dueAt ? moment(test.dueAt) : null;

  if (!dueAt || !dueAt.isValid()) {
    return getStatusTooltip(status);
  }

  const formattedDate = dueAt.format("M/D/YYYY");

  if (status === "due") {
    return `Due on ${formattedDate}`;
  }

  if (status === "missed" || status === "incomplete") {
    return `Due date was ${formattedDate}`;
  }

  return getStatusTooltip(status);
}

export interface TestResultGroupInput {
  status?: string;
  resolved?: boolean;
  exclude?: boolean;
  testVersionId?: number;
  subtestEnabledCount?: number;
}

/**
 * Aggregates result statuses for a group of test results on a calendar day.
 * Priority: Skipped > Fail > Incomplete > Failed & Resolved > Pass
 */
export function aggregateTestResultGroupStatus(items: TestResultGroupInput[]): ResultStatusType | "skipped" | "incomplete" {
  if (!items.length) {
    return "pass";
  }

  const hasSkipped = items.some((t) => t.status === TestRunStatus.notRun);
  if (hasSkipped) {
    return "skipped";
  }

  const hasFail = items.some((t) => t.status === TestRunStatus.fail && !t.resolved);
  if (hasFail) {
    return "fail";
  }

  const firstItem = items[0];
  const enabledSubtestCount = firstItem.subtestEnabledCount ?? 0;
  const uniqueTestVersionIds = new Set(items.map((t) => t.testVersionId)).size;
  if (enabledSubtestCount > 0 && uniqueTestVersionIds < enabledSubtestCount) {
    return "incomplete";
  }

  const hasResolved = items.some((t) => t.status === TestRunStatus.fail && t.resolved);
  if (hasResolved) {
    return "failed_resolved";
  }

  return "pass";
}

const RESULT_DAY_PRIORITY: StatusType[] = ["skipped", "fail", "incomplete", "failed_resolved", "pass"];

function getResultDayPriorityRank(status: StatusType): number {
  const index = RESULT_DAY_PRIORITY.indexOf(status);
  return index >= 0 ? RESULT_DAY_PRIORITY.length - index : 0;
}

/**
 * Aggregates all test results on a calendar day across machine/test groups.
 * Priority: Skipped > Fail > Incomplete > Failed & Resolved > Pass
 */
export function aggregateTestResultDayStatus(items: WidgetTestResult[], dateFormat: string): StatusType {
  if (!items.length) {
    return "pass";
  }

  const groupedByTest: Record<string, WidgetTestResult[]> = {};

  items.forEach((item) => {
    const key = `${item.machineId}_${item.testId}_${moment(item.testDate).format(dateFormat)}`;
    if (!groupedByTest[key]) {
      groupedByTest[key] = [];
    }
    groupedByTest[key].push(item);
  });

  let highestStatus: StatusType = "pass";

  for (const group of Object.values(groupedByTest)) {
    const groupStatus = mapResultStatusToDisplayStatus(aggregateTestResultGroupStatus(group));
    if (getResultDayPriorityRank(groupStatus) > getResultDayPriorityRank(highestStatus)) {
      highestStatus = groupStatus;
    }
  }

  return highestStatus;
}

export function getResultDotStatusFromGroup(items: WidgetTestResult[], dateFormat?: string): StatusType {
  if (dateFormat) {
    return aggregateTestResultDayStatus(items, dateFormat);
  }
  return mapResultStatusToDisplayStatus(aggregateTestResultGroupStatus(items));
}

export function mapResultStatusToDisplayStatus(status: ResultStatusType | "skipped" | "incomplete"): StatusType {
  if (status === "skipped") {
    return "skipped";
  }
  if (status === "incomplete") {
    return "incomplete";
  }
  return status;
}
